import { h, Fragment } from 'preact';
import { useEffect, useState, useMemo, useRef } from 'preact/hooks';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import useWindowSize from 'utils/useWindowSize';
import Storefront from 'utils/storefront';
import { edgesToNodes, formatMoney } from 'utils/storefront/format-resource';
import * as fragments from 'utils/storefront/fragments';
import Image from 'utils/image';
import { useWishlist } from 'utils/gift-reggie/wishlist-context';
import { IconArrow, IconHeart, IconClose, IconBag } from 'utils/icon';
import * as queries from 'utils/storefront/queries';
import ProductBadge from '../product/product-badge';
import ColorSwatches from '../product/color-swatches';
import NotifyMe from '../product/notify-me';

const useHover = (setSelectedVariant) => {
  const handlers = {
    onMouseLeave: () => {
      setSelectedVariant(false);
    },
  };

  return handlers;
};

const ProductCard = ({
  product,
  showSelectedColor = false,
  swipeSlider = true,
  lazyload = true,
  addToCartAttributeName = null,
}) => {
  const storefront = Storefront.getInstance();
  const [swatchProducts, setSwatchProducts] = useState([]);
  const [productState, setProductState] = useState(product);
  const [isAddedToWishlist, setIsAddedToWishlist] = useState(false);
  const [variantInWishlist, setVariantInWishlist] = useState(false);
  const { isMobile, isTablet } = useWindowSize();
  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const productCardRef = useRef(null);
  const updateSelectedVariant = (variant) => {
    setSelectedVariant(variant);
  };
  const technicalDataEl = document.querySelector('[data-technical]');
  const isLoggedIn =
    technicalDataEl?.innerHTML &&
    JSON.parse(technicalDataEl.innerHTML)?.customer;

  const [isWishlistVisible, setIsWishlistVisible] = useState(false);
  const [animationDirection, setAnimationDirection] = useState('');
  const hoverHandlers = useHover(setSelectedVariant);
  const availableColorsArray = product.metafields?.availableColors || [];

  const getSwatchProducts = async () => {
    const swatchProductHandles =
      product?.metafields?.swatchProducts ||
      (product?.swatchProducts?.value &&
        JSON.parse(product.swatchProducts.value));

    if ((swatchProductHandles || []).length === 0) return null;

    let swatchProductsSubQuery = '';
    swatchProductHandles.map(
      // eslint-disable-next-line no-return-assign
      (handle, index) =>
        // eslint-disable-next-line max-len
        (swatchProductsSubQuery += `p${index}: productByHandle(handle: "${handle}") {
            ${fragments.product}
          }`),
    );

    const response = await storefront.request({
      // eslint-disable-next-line max-len
      query: `query getSwatchProducts @inContext(country: ${window.Shopify.country}){
        ${swatchProductsSubQuery}
      }`,
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'product-grid/product-grid.js: Storefront API request failed',
        response,
      );
      return [];
    }

    const data = Object.values(response.data || {})
      .filter((item) => item)
      .map((responseProduct) => ({
        ...responseProduct,
        images: edgesToNodes(responseProduct.images, 'image'),
        variants: edgesToNodes(responseProduct.variants, 'variant'),
      }));

    setSwatchProducts(data);
    return data;
  };

  const handleAddToWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoggedIn) {
      addToWishlist(
        (
          (productState.variants?.edges &&
            productState?.variants?.edges[0]?.node) ||
          productState?.variants[0]
        )?.id.split('/ProductVariant/')[1],
      );
      setIsAddedToWishlist(true);
    } else {
      window.localStorage.removeItem('wishlist');
      window.sessionStorage.setItem(
        'addToWishlist',
        (
          (productState.variants?.edges &&
            productState?.variants?.edges[0]?.node) ||
          productState?.variants[0]
        )?.id.split('/ProductVariant/')[1],
      );
      setIsWishlistVisible(true);
      setAnimationDirection('slide-up-visible');
    }
  };

  const closeWishlist = (e) => {
    // Close the wishlist when the close button is clicked
    e.preventDefault();
    setAnimationDirection('slide-down-visible');
    setIsWishlistVisible(false);
  };

  const handleRemoveFromWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoggedIn) {
      removeFromWishlist(variantInWishlist.variant_id);
      setIsAddedToWishlist(false);
    } else {
      window.localStorage.removeItem('wishlist');
      window.location.pathname = '/account/login';
    }
  };

  useEffect(() => {
    setIsAddedToWishlist(false);
    if (!isLoggedIn) {
      window.localStorage.removeItem('wishlist');
      return;
    }
    const isProductInList = wishlist.find(
      (item) => `${item.product_id}` === productState.id.split('/Product/')[1],
    );

    if (isProductInList) {
      setVariantInWishlist(isProductInList);
      setIsAddedToWishlist(true);
    }
  }, [productState, wishlist]);

  useEffect(() => {
    if (
      !product.metafields?.swatchProducts?.length > 1 &&
      !(
        product?.swatchProducts?.value &&
        JSON.parse(product.swatchProducts.value)
      )?.length > 1
    )
      return;
    getSwatchProducts();
  }, [product]);

  const handleSwatchClick = (colorData) => {
    setProductState(
      swatchProducts?.find((swatchProduct) =>
        swatchProduct.options.find(
          (option) =>
            option.name === 'Color' && option.values.includes(colorData.name),
        ),
      ) || [],
    );
  };

  const openQuickAdd = (e) => {
    e.preventDefault();
    const openQuickAddEvent = new CustomEvent('openQuickAdd', {
      detail: {
        product: productState.handle,
        location: addToCartAttributeName,
      },
    });
    window.dispatchEvent(openQuickAddEvent);
  };

  const productCard = useMemo(() => {
    if (!productState) return null;
    const url = `/products/${productState.handle}`;
    const badgeTag = (productState?.tags || []).find((tag) =>
      tag.includes('badge'),
    );

    return (
      <Fragment>
        <div className="group relative" {...hoverHandlers}>
          <div className="relative">
            <a
              className="relative group block no-underline mb-2 lg:mb-0 aspect-[2/3]"
              href={url}
            >
              <span className="w-full h-full hidden lg:block relative">
                {productState.images[0] && (
                  <Image
                    {...productState.images[0]}
                    srcWidths={{ sm: 640 }}
                    classes="object-cover h-full"
                    lazyload={lazyload}
                  />
                )}
                {productState.images[1] && (
                  <Image
                    {...productState.images[1]}
                    lazyload={lazyload}
                    srcWidths={{ sm: 640 }}
                    classes="object-cover h-full absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100"
                  />
                )}
              </span>
              <Swiper
                pagination={{
                  el: '.swiper-pagination',
                  type: 'bullets',
                  bulletClass:
                    'swiper-pagination-bullet h-full flex-grow block',
                  bulletActiveClass:
                    'swiper-pagination-bullet-active bg-gray-800',
                }}
                modules={[Pagination]}
                allowTouchMove={true}
                loop={swipeSlider}
                className="w-full h-full lg:!hidden max-h-full z-1"
              >
                {swipeSlider
                  ? productState.images.map((img, index) => (
                      <SwiperSlide key={index}>
                        <Image
                          {...img}
                          srcWidths={{ sm: 640 }}
                          lazyload={lazyload}
                          classes="h-full object-cover"
                        />
                      </SwiperSlide>
                    ))
                  : productState.images.slice(0, 1).map((img, index) => (
                      <SwiperSlide key={index}>
                        <Image
                          {...img}
                          lazyload={lazyload}
                          srcWidths={{ sm: 640 }}
                          classes="h-full object-cover"
                        />
                      </SwiperSlide>
                    ))}
                <div
                  className={
                    swipeSlider
                      ? 'absolute flex bottom-0 left-0 w-full swiper-pagination h-0.5 z-1'
                      : 'hidden'
                  }
                ></div>
              </Swiper>
              <button
                className="hidden lg:group-hover:block absolute top-3 right-3 z-1 pointer-events-auto w-[20px] h-[18px]"
                onClick={
                  isAddedToWishlist
                    ? handleRemoveFromWishlist
                    : handleAddToWishlist
                }
              >
                <IconHeart filled={isAddedToWishlist} />
              </button>
              <div
                className={`fixed left-0 right-0 bg-gray-0 w-full bottom-0
                pt-[38px] pb-[26px] px-[20px] flex flex-col items-center
                justify-center z-2 border-t border-gray-400
                lg:absolute lg:border-t-0 ${animationDirection}`}
                data-wishlist
              >
                <h2 className="text-center pb-[15px]">
                  {window.TMW.Strings.wishlist.saveToWishlist}
                </h2>
                <p className="body-2 text-center pb-[30px]">
                  {window.TMW.Strings.wishlist.wishlistText}
                </p>
                <a
                  className="button button--primary mb-[12px] no-underline block w-full text-center lg:w-fit"
                  // eslint-disable-next-line max-len
                  href={`${
                    Shopify.routes.root
                  }account/login?return_url=${encodeURIComponent(
                    window.location.pathname,
                  )}`}
                >
                  {window.TMW.Strings.wishlist.wishlistSignIn}
                </a>
                <a
                  className="text-link uppercase block text-center h7"
                  href={`${
                    Shopify.routes.root
                  }account/login?return_url=${encodeURIComponent(
                    window.location.pathname,
                  )}`}
                >
                  {window.TMW.Strings.wishlist.wishlistCreateAccount}
                </a>
                <div
                  className="icon-close absolute top-[16px] right-[16px]"
                  data-wishlist-close
                  onClick={closeWishlist}
                >
                  <IconClose />
                </div>
              </div>
            </a>
            <div
              className="pointer-events-auto cursor-pointer w-[50px] h-[50px] flex items-end justify-start absolute left-0 bottom-0 pl-3 pb-3 lg:hidden z-1"
              onClick={openQuickAdd}
            >
              <button className="flex justify-center items-center rounded-full bg-white w-[30px] h-[30px]">
                <IconBag className="w-[15px]" />
              </button>
            </div>
            <div
              className="lg:hidden absolute bottom-0 right-0 pb-3 pr-3 z-1 w-[50px] h-[50px] flex justify-end items-end pointer-events-auto cursor-pointer"
              onClick={
                isAddedToWishlist
                  ? handleRemoveFromWishlist
                  : handleAddToWishlist
              }
            >
              <button>
                <IconHeart filled={isAddedToWishlist} />
              </button>
            </div>
            {selectedVariant && !selectedVariant?.availableForSale ? (
              <NotifyMe
                product={productState}
                classes={
                  'absolute bottom-0 bg-gray-0 w-full py-[12px] px-[9px] z-2'
                }
                activeVariant={selectedVariant}
                isProductCard={true}
              />
            ) : null}
          </div>
          {(product.optionsWithValues || product.options || []).find(
            (opt) => opt.name.toLowerCase() === 'size',
          ) &&
            !isTablet &&
            !isMobile && (
              <div className="opacity-0 h-0 group-hover:h-auto group-hover:opacity-100 group-hover:mt-3.5 transition-all duration-300 ease-in-out">
                <OptionsSelector
                  product={productState}
                  addToCartAttributeName={addToCartAttributeName}
                  updateSelectedVariant={updateSelectedVariant}
                />
              </div>
            )}

          {badgeTag && <ProductBadge badge={badgeTag} />}

          <div className="text-center px-2 pb-2.5 lg:pt-2.5">
            <h3 className="h6--medium no-underline text-gray-900">
              {productState.vendor}
            </h3>
            <h4>
              <a className="body-2 no-underline text-gray-900" href={url}>
                {productState.title}
              </a>
            </h4>
            <p className="h6 text-text-subdued">
              {parseFloat(
                productState.compareAtPriceRange.minVariantPrice.amount,
              ) > 0 && (
                <del className="text-gray-600 mr-2">
                  {formatMoney(
                    productState.compareAtPriceRange.minVariantPrice,
                    false,
                    true,
                  )}
                </del>
              )}
              <span
                className={`${
                  parseFloat(
                    productState.compareAtPriceRange.minVariantPrice.amount,
                  ) > 0
                    ? 'text-sale'
                    : ''
                }`}
              >
                {formatMoney(
                  productState.priceRange.minVariantPrice,
                  false,
                  true,
                )}
              </span>
            </p>
            <h5 className="h7 text-core-3">
              {productState.metafields?.promotionalCopy ||
                productState?.promotionalCopy?.value}
            </h5>
          </div>
        </div>
      </Fragment>
    );
  }, [productState, isAddedToWishlist, selectedVariant, isWishlistVisible]);

  return (
    <article
      className="relative product-card"
      data-product-card
      ref={productCardRef}
    >
      {productCard}
      <ColorSwatches
        product={product}
        availableColorsArray={availableColorsArray}
        showSelected={showSelectedColor}
        activeColor={(
          (productState.options || []).find((option) => option.name === 'Color')
            ?.values[0] ||
          (product.optionsWithValues || []).find(
            (option) => option.name === 'Color',
          )?.selected_value ||
          ''
        ).toLowerCase()}
        maxSwatches={isMobile ? 3 : 5}
        onClickSwatch={handleSwatchClick}
      />
    </article>
  );
};

const OptionsSelector = ({
  product,
  updateSelectedVariant,
  addToCartAttributeName,
}) => {
  const variantIsAvailableClassList =
    'relative hover:before:w-full hover:before:absolute ' +
    'hover:before:bottom-0 hover:before:left-1/2 ' +
    'hover:before:-translate-x-1/2 hover:before:h-[1px] ' +
    'hover:before:bg-gray-900';
  const variantIsUnavailableClassList =
    'text-text-disabled font-normal relative ' +
    'after:absolute after:h-[1px] after:bg-text-disabled ' +
    'after:top-1/2 after:-left-1 after:-right-1 ' +
    'after:-translate-y-1/2';

  const handleAddToCart = async (e, variant) => {
    e.preventDefault();

    const storefront = Storefront.getInstance();
    if (variant.availableForSale && !product.hideAddToCart) {
      const { finalSaleTag, discountCodeTag, dropShipTag } = JSON.parse(
        document.querySelector('[data-global-settings]').textContent,
      );

      const responseByHandleUSD = await storefront.request({
        query: queries.productByHandleUSD(),
        variables: {
          handle: product.handle,
        },
      });

      const usdCompareAtPrice =
        responseByHandleUSD?.data?.productByHandle?.compareAtPriceRange
          ?.minVariantPrice?.amount || '0';

      const payload = {
        merchandiseId: variant.id,
        quantity: 1,
        attributes: [],
      };

      if (product.tags.includes(finalSaleTag)) {
        payload.attributes.push({
          key: '_tag_:final-sale',
          value: finalSaleTag.replace(' ', ''),
        });
      }
      if (product.tags.includes(discountCodeTag)) {
        payload.attributes.push({
          key: '_tag_:discount-code',
          value: discountCodeTag,
        });
      }

      if (product.tags.includes(dropShipTag)) {
        payload.attributes.push({
          key: '_tag_:drop-ship',
          value: `${dropShipTag.replace(' ', '')}`,
        });
      }
      if (
        variant.compareAtPrice &&
        parseFloat(variant.compareAtPrice.amount) > 0
      ) {
        payload.attributes.push({
          key: '_compare_at_price',
          value: parseFloat(usdCompareAtPrice).toFixed(2),
        });
      }

      const addToCartEvent = new CustomEvent('cart:add', {
        detail: {
          payload,
          origin: 'quick_add',
        },
      });
      document.dispatchEvent(addToCartEvent);
      updateSelectedVariant(variant);
    } else {
      updateSelectedVariant(variant);
    }
  };

  return (
    product?.variants?.length && (
      <div className="flex w-full">
        {product.variants.length > 3 && (
          <button className="swiper-button-prev min-w-[26px] h-8 items-center justify-start flex">
            <IconArrow />
          </button>
        )}
        <Swiper
          modules={[Navigation]}
          navigation={
            product.variants.length > 3
              ? {
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                  disabledClass: 'hidden',
                }
              : false
          }
          slidesPerView={2}
          breakpoints={{
            1300: {
              slidesPerView: 3,
            },
          }}
          className="flex-grow"
        >
          {product.variants.map((variant, index) => (
            <SwiperSlide
              className="flex items-center justify-center h-8 mx-auto"
              key={index}
            >
              <button
                onClick={(e) => handleAddToCart(e, variant)}
                className={`w-full`}
                data-add-to-cart={addToCartAttributeName}
              >
                <span
                  className={`h7--medium ${
                    variant.availableForSale && !product.hideAddToCart
                      ? variantIsAvailableClassList
                      : variantIsUnavailableClassList
                  }`}
                >
                  {
                    variant.selectedOptions.find(
                      (opt) => opt.name.toLowerCase() === 'size',
                    ).value
                  }
                </span>
              </button>
            </SwiperSlide>
          ))}
        </Swiper>
        {product.variants.length > 3 && (
          <button className="swiper-button-next min-w-[26px] h-8 items-center justify-end flex">
            <IconArrow direction="right" />
          </button>
        )}
      </div>
    )
  );
};

export default ProductCard;
